import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";

export const Route = createFileRoute("/unsubscribe")({
  head: () => ({
    meta: [
      { title: "Unsubscribe — RanchPlanner" },
      { name: "description", content: "Unsubscribe from RanchPlanner emails. Confirm your preference and we'll stop sending you messages." },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: UnsubscribePage,
  validateSearch: (search: Record<string, unknown>) => ({
    token: typeof search.token === "string" ? search.token : "",
  }),
});

type Status = "loading" | "ready" | "already" | "invalid" | "submitting" | "done" | "error";

function UnsubscribePage() {
  const { token } = Route.useSearch();
  const [status, setStatus] = useState<Status>("loading");
  const [email, setEmail] = useState<string | null>(null);

  useEffect(() => {
    if (!token) { setStatus("invalid"); return; }
    fetch(`/email/unsubscribe?token=${encodeURIComponent(token)}`)
      .then(async (r) => {
        const j = await r.json().catch(() => ({}));
        if (!r.ok) { setStatus("invalid"); return; }
        if (j.alreadyUnsubscribed || j.already_unsubscribed) { setStatus("already"); setEmail(j.email || null); return; }
        setEmail(j.email || null);
        setStatus("ready");
      })
      .catch(() => setStatus("error"));
  }, [token]);

  const onConfirm = async () => {
    setStatus("submitting");
    try {
      const r = await fetch("/email/unsubscribe", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token }),
      });
      if (!r.ok) { setStatus("error"); return; }
      setStatus("done");
    } catch { setStatus("error"); }
  };

  return (
    <div className="mx-auto max-w-md px-6 py-16 text-center">
      <h1 className="text-3xl font-bold mb-4" style={{ fontFamily: "'Syne', sans-serif" }}>Unsubscribe</h1>
      {status === "loading" && <p className="text-muted-foreground">Checking your link…</p>}
      {status === "invalid" && <p className="text-muted-foreground">This unsubscribe link is invalid or has expired.</p>}
      {status === "already" && <p className="text-muted-foreground">{email || "You"} is already unsubscribed. ✅</p>}
      {status === "ready" && (
        <>
          <p className="mb-6 text-muted-foreground">
            Click below to stop receiving emails{email ? ` at ${email}` : ""} from RanchPlanner.
          </p>
          <button onClick={onConfirm} className="rounded-full bg-primary px-6 py-3 font-bold text-primary-foreground hover:bg-primary/90">
            Confirm unsubscribe
          </button>
        </>
      )}
      {status === "submitting" && <p className="text-muted-foreground">Unsubscribing…</p>}
      {status === "done" && <p className="text-muted-foreground">You're unsubscribed. Sorry to see you go! 👋</p>}
      {status === "error" && <p className="text-muted-foreground">Something went wrong. Please try again later.</p>}
    </div>
  );
}